import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { GitProvider } from 'consts/git';
import { RepositoryClient } from '../httpClients/RepositoryClient';
import { GetWebhookUrlResponse } from '../httpClients/RepositoryClient/api.schema';
import { QUERY_KEYS } from './consts';

type OmittedQueryOptions = Omit<
  UseQueryOptions<GetWebhookUrlResponse, Error, string | null>,
  'queryKey' | 'queryFn' | 'select'
>;

/**
 * Fetches the git integration webhook URL for a given provider from the backend.
 * The backend is the source of truth across MT, ST, and self-hosted deployments.
 *
 * The selected `data` is `string | null`:
 * - string: use this URL in the GitHub App manifest's hook_attributes.
 * - null: BE doesn't know yet (e.g. ST + Redis empty); caller should fall back to its template.
 *
 * @param provider - The git provider (e.g. 'github')
 * @param options - Optional react-query options
 * @returns A react-query result whose `data` is the webhook URL string, or `null` when the backend has no value yet.
 */
export const useGitIntegrationWebhookUrlQuery = (
  provider: GitProvider,
  options?: OmittedQueryOptions,
) =>
  useQuery({
    queryKey: [QUERY_KEYS.GIT_INTEGRATION_WEBHOOK_URL, provider],
    queryFn: () => RepositoryClient.getGitIntegrationWebhookUrl(provider),
    select: (data) => data.webhookUrl,
    staleTime: 0,
    gcTime: 0,
    ...options,
  });
